import { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { toast } from 'sonner';
import api from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { LogOut, Users, Activity, Shield, Dumbbell, Megaphone, Download, FileText, Plus, Edit, Trash2, UserCog, Building2, CheckCircle, XCircle, Eye, KeyRound, Key, Lock, Search } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const AdminDashboard = () => {
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [accessLogs, setAccessLogs] = useState([]);
  const [payments, setPayments] = useState([]);
  const [subscriptionAnalytics, setSubscriptionAnalytics] = useState(null);
  const [announcements, setAnnouncements] = useState([]);
  const [bankTransfers, setBankTransfers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [announcementForm, setAnnouncementForm] = useState({ title: '', content: '', priority: 'normal', is_published: true });
  const [editingAnnouncement, setEditingAnnouncement] = useState(null);
  const [announcementDialogOpen, setAnnouncementDialogOpen] = useState(false);
  
  // Password reset state
  const [resetPasswordDialogOpen, setResetPasswordDialogOpen] = useState(false);
  const [selectedUserForReset, setSelectedUserForReset] = useState(null);
  const [newPasswordForUser, setNewPasswordForUser] = useState('');
  const [resettingPassword, setResettingPassword] = useState(false);

  // Access code generation state
  const [accessCodeDialogOpen, setAccessCodeDialogOpen] = useState(false);
  const [selectedUserForAccess, setSelectedUserForAccess] = useState(null);
  const [generatedAccessCode, setGeneratedAccessCode] = useState(null);
  const [generatingAccess, setGeneratingAccess] = useState(false);
  
  // Subscription plans management state
  const [subscriptionPlans, setSubscriptionPlans] = useState([]);
  const [plansDialogOpen, setPlansDialogOpen] = useState(false);
  const [editingPlan, setEditingPlan] = useState(null);
  const [planForm, setPlanForm] = useState({ name: '', price: '', duration_days: '', description: '' });

  // Shared daily code state
  const [sharedCodeDialogOpen, setSharedCodeDialogOpen] = useState(false);
  const [sharedCodeHours, setSharedCodeHours] = useState(24);
  const [sharedCodeMessage, setSharedCodeMessage] = useState('');
  const [customCode, setCustomCode] = useState('');
  const [useCustomCode, setUseCustomCode] = useState(true);
  const [generatedSharedCode, setGeneratedSharedCode] = useState(null);
  const [generatingSharedCode, setGeneratingSharedCode] = useState(false);
  const [activeSharedCode, setActiveSharedCode] = useState(null);
  
  // Change plan dialog state
  const [changePlanDialogOpen, setChangePlanDialogOpen] = useState(false);
  const [selectedUserForPlan, setSelectedUserForPlan] = useState(null);
  const [selectedPlan, setSelectedPlan] = useState('');
  const [planDays, setPlanDays] = useState('');

  useEffect(() => {
    fetchAdminData();
    fetchActiveSharedCode();
    fetchSubscriptionPlans();
  }, []);

  const fetchActiveSharedCode = async () => {
    try {
      const response = await api.get('/access/shared-code');
      if (response.data.has_code) {
        setActiveSharedCode(response.data);
      }
    } catch (error) {
      console.error('Failed to fetch shared code');
    }
  };
  
  const fetchSubscriptionPlans = async () => {
    try {
      const response = await api.get('/admin/plans');
      setSubscriptionPlans(response.data || []);
    } catch (error) {
      console.error('Failed to fetch plans:', error);
    }
  };

  const handleUpdatePlan = async () => {
    if (!editingPlan) return;
    try {
      const params = new URLSearchParams();
      if (planForm.name) params.append('name', planForm.name);
      if (planForm.price) params.append('price', planForm.price);
      if (planForm.duration_days) params.append('duration_days', planForm.duration_days);
      if (planForm.description) params.append('description', planForm.description);
      
      await api.patch(`/admin/plans/${editingPlan.id}?${params.toString()}`);
      toast.success('Plan updated successfully');
      setPlansDialogOpen(false);
      setEditingPlan(null);
      setPlanForm({ name: '', price: '', duration_days: '', description: '' });
      fetchSubscriptionPlans();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update plan');
    }
  };

  const fetchAdminData = async () => {
    try {
      // Fetch data with individual error handling to prevent complete failure
      const results = await Promise.allSettled([
        api.get('/admin/stats'),
        api.get('/admin/users'),
        api.get('/admin/access-logs?limit=50'),
        api.get('/admin/payments?limit=50'),
        api.get('/admin/subscription-analytics'),
        api.get('/admin/announcements'),
        api.get('/admin/bank-transfers')
      ]);

      // Extract data with fallbacks
      const getData = (result, fallback) => result.status === 'fulfilled' ? result.value.data : fallback;
      
      setStats(getData(results[0], { active_members: 0, inactive_members: 0, total_workouts: 0 }));
      setUsers(getData(results[1], []));
      setAccessLogs(getData(results[2], []));
      setPayments(getData(results[3], []));
      setSubscriptionAnalytics(getData(results[4], { by_plan: {}, by_status: {} }));
      setAnnouncements(getData(results[5], []));
      setBankTransfers(getData(results[6], []));

      // Log any failed requests for debugging
      const failedRequests = results.filter(r => r.status === 'rejected');
      if (failedRequests.length > 0) {
        console.error('Some admin data requests failed:', failedRequests.map(r => r.reason?.message || 'Unknown error'));
        if (failedRequests.length === results.length) {
          toast.error('Failed to load admin data. Please check your connection and try again.');
        } else {
          toast.warning(`Some data could not be loaded (${failedRequests.length} requests failed)`);
        }
      }
    } catch (error) {
      console.error('Admin data fetch error:', error);
      toast.error('Failed to load admin data: ' + (error.message || 'Unknown error'));
      // Set fallback empty data so dashboard still renders
      setStats({ active_members: 0, inactive_members: 0, total_workouts: 0 });
      setUsers([]);
      setAccessLogs([]);
      setPayments([]);
      setSubscriptionAnalytics({ by_plan: {}, by_status: {} });
      setAnnouncements([]);
      setBankTransfers([]);
    } finally {
      setLoading(false);
    }
  };

  const handleApproveBankTransfer = async (transferId) => {
    try {
      await api.patch(`/admin/bank-transfers/${transferId}/approve`);
      toast.success('Bank transfer approved and subscription activated');
      fetchAdminData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to approve transfer');
    }
  };

  const handleRejectBankTransfer = async (transferId) => {
    const notes = prompt('Enter rejection reason (optional):');
    try {
      await api.patch(`/admin/bank-transfers/${transferId}/reject?admin_notes=${encodeURIComponent(notes || '')}`);
      toast.success('Bank transfer rejected');
      fetchAdminData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reject transfer');
    }
  };

  const handleUpdateSubscription = async (userId, status, plan) => {
    try {
      await api.patch(`/admin/users/${userId}/subscription?status=${status}${plan ? `&plan=${plan}` : ''}`);
      toast.success('Subscription updated');
      fetchAdminData();
    } catch (error) {
      toast.error('Failed to update subscription');
    }
  };

  // Derived: case-insensitive filter based on searchQuery
  const q = (searchQuery || '').toLowerCase().trim();
  const matches = (...vals) =>
    !q || vals.some((v) => (v ?? '').toString().toLowerCase().includes(q));
  const filteredUsers = users.filter((u) =>
    matches(u.full_name, u.email, u.phone, u.role, u.subscription_status, u.subscription_plan)
  );
  const filteredBankTransfers = bankTransfers.filter((t) =>
    matches(t.user_name, t.user_email, t.depositor_name, t.plan_name, t.status)
  );
  const filteredPayments = payments.filter((p) =>
    matches(p.user_name, p.user_email, p.payment_status, p.amount)
  );
  const filteredAccessLogs = accessLogs.filter((l) =>
    matches(l.user_name, l.access_method, l.access_status)
  );

  const handleChangePlan = async () => {
    if (!selectedUserForPlan || !selectedPlan) return;
    try {
      const params = new URLSearchParams({ plan: selectedPlan });
      if (planDays) {
        params.append('extend_days', planDays);
      }
      await api.patch(`/admin/users/${selectedUserForPlan.id}/plan?${params.toString()}`);
      toast.success(`Plan changed to ${selectedPlan}`);
      setChangePlanDialogOpen(false);
      setSelectedUserForPlan(null);
      setSelectedPlan('');
      setPlanDays('');
      fetchAdminData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to change plan');
    }
  };

  const handleUpdateUserRole = async (userId, role) => {
    try {
      await api.patch(`/admin/users/${userId}/role?role=${role}`);
      toast.success('User role updated');
      fetchAdminData();
    } catch (error) {
      toast.error('Failed to update user role');
    }
  };

  const handleCreateAnnouncement = async () => {
    try {
      await api.post('/admin/announcements', announcementForm);
      toast.success('Announcement created');
      setAnnouncementForm({ title: '', content: '', priority: 'normal', is_published: true });
      setAnnouncementDialogOpen(false);
      fetchAdminData();
    } catch (error) {
      toast.error('Failed to create announcement');
    }
  };

  const handleUpdateAnnouncement = async () => {
    if (!editingAnnouncement) return;
    try {
      await api.put(`/admin/announcements/${editingAnnouncement.id}`, announcementForm);
      toast.success('Announcement updated');
      setEditingAnnouncement(null);
      setAnnouncementForm({ title: '', content: '', priority: 'normal', is_published: true });
      setAnnouncementDialogOpen(false);
      fetchAdminData();
    } catch (error) {
      toast.error('Failed to update announcement');
    }
  };

  const handleDeleteAnnouncement = async (id) => {
    if (!confirm('Are you sure you want to delete this announcement?')) return;
    try {
      await api.delete(`/admin/announcements/${id}`);
      toast.success('Announcement deleted');
      fetchAdminData();
    } catch (error) {
      toast.error('Failed to delete announcement');
    }
  };

  const handleResetUserPassword = async () => {
    if (!selectedUserForReset || !newPasswordForUser) {
      toast.error('Please enter a new password');
      return;
    }
    if (newPasswordForUser.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }
    
    setResettingPassword(true);
    try {
      await api.post(`/admin/reset-user-password?user_id=${selectedUserForReset.id}&new_password=${encodeURIComponent(newPasswordForUser)}`);
      toast.success(`Password reset for ${selectedUserForReset.email}`);
      setResetPasswordDialogOpen(false);
      setSelectedUserForReset(null);
      setNewPasswordForUser('');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reset password');
    } finally {
      setResettingPassword(false);
    }
  };

  const handleGenerateAccessCode = async (accessType) => {
    if (!selectedUserForAccess) return;
    
    setGeneratingAccess(true);
    try {
      const response = await api.post(`/admin/generate-user-access/${selectedUserForAccess.id}?access_type=${accessType}`);
      setGeneratedAccessCode(response.data);
      toast.success(`Access code generated for ${selectedUserForAccess.full_name}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to generate access code');
    } finally {
      setGeneratingAccess(false);
    }
  };

  const handleGenerateSharedCode = async () => {
    // Validate custom code if using manual input (6-7 digits)
    if (useCustomCode && customCode) {
      if (!/^\d{6,7}$/.test(customCode)) {
        toast.error('Code must be 6-7 digits');
        return;
      }
    }
    
    setGeneratingSharedCode(true);
    try {
      let url = `/admin/generate-shared-code?hours_valid=${sharedCodeHours}&message=${encodeURIComponent(sharedCodeMessage || 'Daily access code')}`;
      if (useCustomCode && customCode) {
        url += `&custom_code=${customCode}`;
      }
      
      const response = await api.post(url);
      setGeneratedSharedCode(response.data);
      setActiveSharedCode({
        has_code: true,
        code: response.data.code,
        expires_at: response.data.expires_at,
        message: sharedCodeMessage || 'Daily access code'
      });
      toast.success(response.data.was_custom ? 'Custom code set successfully!' : 'Code generated successfully!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to set access code');
    } finally {
      setGeneratingSharedCode(false);
    }
  };

  const handleExport = async (type, format = 'csv') => {
    try {
      const response = await api.get(`/admin/export/${type}?format=${format}`, {
        responseType: format === 'csv' ? 'blob' : 'json'
      });
      
      if (format === 'csv') {
        const blob = new Blob([response.data], { type: 'text/csv' });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `${type}_export.csv`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        a.remove();
        toast.success(`${type} data exported`);
      } else {
        // JSON export
        const dataStr = JSON.stringify(response.data, null, 2);
        const blob = new Blob([dataStr], { type: 'application/json' });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `${type}_export.json`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        a.remove();
        toast.success(`${type} data exported`);
      }
    } catch (error) {
      toast.error('Failed to export data');
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    navigate('/login');
    toast.success('Logged out successfully');
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-foreground">Loading admin dashboard...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background p-4 sm:p-6 md:p-12">
      <div className="container mx-auto max-w-7xl">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6 sm:mb-8">
          <h1 className="text-2xl sm:text-3xl md:text-4xl font-heading font-black uppercase tracking-tight" data-testid="admin-dashboard-title">
            Admin Dashboard
          </h1>
          <div className="flex gap-2 sm:gap-3 w-full sm:w-auto">
            <Link to="/" className="flex-1 sm:flex-none">
              <Button
                variant="outline"
                size="sm"
                className="w-full sm:w-auto border-primary text-primary hover:bg-primary hover:text-white"
              >
                <span className="hidden sm:inline">Back to Home</span>
                <span className="sm:hidden">Home</span>
              </Button>
            </Link>
            <Button
              variant="outline"
              size="sm"
              onClick={handleLogout}
              className="flex-1 sm:flex-none border-destructive text-destructive hover:bg-destructive hover:text-white"
              data-testid="admin-logout-btn"
            >
              <LogOut className="w-4 h-4 sm:mr-2" />
              <span className="hidden sm:inline">Logout</span>
            </Button>
          </div>
        </div>

        {/* Export Actions */}
        <div className="flex flex-wrap gap-2 mb-4 sm:mb-6">
          <Button
            variant="outline"
            size="sm"
            onClick={() => handleExport('users', 'csv')}
            className="text-xs sm:text-sm border-primary text-primary hover:bg-primary hover:text-white"
            data-testid="export-users-btn"
          >
            <Download className="w-3 h-3 sm:w-4 sm:h-4 mr-1 sm:mr-2" />
            <span className="hidden sm:inline">Export Users (CSV)</span>
            <span className="sm:hidden">Users</span>
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => handleExport('subscriptions', 'csv')}
            className="text-xs sm:text-sm border-primary text-primary hover:bg-primary hover:text-white"
            data-testid="export-subscriptions-btn"
          >
            <Download className="w-3 h-3 sm:w-4 sm:h-4 mr-1 sm:mr-2" />
            <span className="hidden sm:inline">Export Subscriptions (CSV)</span>
            <span className="sm:hidden">Subs</span>
          </Button>
        </div>

        {/* Shared Daily Code Section */}
        <Card className="bg-gradient-to-r from-green-500/20 to-blue-500/20 border-green-500/30 mb-6">
          <CardContent className="p-4 sm:p-6">
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
              <div className="flex items-center gap-3">
                <div className="p-3 bg-green-500/20 rounded-full">
                  <Key className="w-6 h-6 text-green-500" />
                </div>
                <div>
                  <h3 className="font-bold text-lg">Daily Access Code</h3>
                  <p className="text-sm text-muted-foreground">Set a code visible to all active subscription members</p>
                </div>
              </div>
              
              <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3 w-full sm:w-auto">
                {activeSharedCode?.has_code && (
                  <div className="bg-card/50 border border-green-500/50 rounded-lg px-4 py-2 text-center">
                    <p className="text-xs text-muted-foreground">Current Code</p>
                    <p className="text-2xl font-mono font-bold text-green-400 tracking-widest" data-testid="active-shared-code">
                      {activeSharedCode.code}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      Expires: {new Date(activeSharedCode.expires_at).toLocaleString()}
                    </p>
                  </div>
                )}
                
                <Button
                  onClick={() => {
                    setSharedCodeDialogOpen(true);
                    setGeneratedSharedCode(null);
                    setSharedCodeMessage('');
                    setCustomCode('');
                  }}
                  className="bg-green-600 hover:bg-green-700 w-full sm:w-auto"
                  data-testid="generate-shared-code-btn"
                >
                  <Key className="w-4 h-4 mr-2" />
                  {activeSharedCode?.has_code ? 'Change Code' : 'Set New Code'}
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Stats Overview */}
        <div className="grid grid-cols-2 md:grid-cols-2 gap-3 sm:gap-4 mb-6 sm:mb-8">
          <Card className="bg-card/50 border-white/10">
            <CardHeader className="p-3 sm:p-6 pb-2 sm:pb-2">
              <CardTitle className="flex items-center text-sm sm:text-lg">
                <Users className="w-4 h-4 sm:w-5 sm:h-5 mr-1 sm:mr-2 text-primary" />
                <span className="hidden sm:inline">Active Members</span>
                <span className="sm:hidden">Active</span>
              </CardTitle>
            </CardHeader>
            <CardContent className="p-3 sm:p-6 pt-0">
              <p className="text-xl sm:text-3xl font-bold" data-testid="admin-active-members">{stats?.active_members || 0}</p>
              <p className="text-xs sm:text-sm text-muted-foreground mt-1 hidden sm:block">
                Inactive: {stats?.inactive_members || 0}
              </p>
            </CardContent>
          </Card>

          <Card className="bg-card/50 border-white/10">
            <CardHeader>
              <CardTitle className="flex items-center text-lg">
                <Dumbbell className="w-5 h-5 mr-2 text-secondary" />
                Total Workouts
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-3xl font-bold">{stats?.total_workouts || 0}</p>
              <p className="text-sm text-muted-foreground mt-1">
                Members are active
              </p>
            </CardContent>
          </Card>
        </div>

        {/* Subscription Analytics */}
        <div className="grid grid-cols-1 gap-6 mb-8">
          {subscriptionAnalytics && (
            <Card className="bg-card/50 border-white/10">
              <CardHeader>
                <CardTitle className="flex items-center font-heading uppercase">
                  <Activity className="w-5 h-5 mr-2" />
                  Subscription Distribution
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <p className="text-sm font-semibold mb-2">By Plan:</p>
                    {(subscriptionAnalytics?.plan_distribution || []).map((item, idx) => (
                      <div key={idx} className="flex justify-between items-center mb-2">
                        <span className="text-sm">{item.plan || 'No Plan'}</span>
                        <Badge variant="outline">{item.count} users</Badge>
                      </div>
                    ))}
                  </div>
                  <div>
                    <p className="text-sm font-semibold mb-2">By Status:</p>
                    {(subscriptionAnalytics?.status_distribution || []).map((item, idx) => (
                      <div key={idx} className="flex justify-between items-center mb-2">
                        <span className="text-sm capitalize">{item.status}</span>
                        <Badge 
                          variant={item.status === 'active' ? 'default' : 'secondary'}
                          className={item.status === 'active' ? 'bg-green-500' : ''}
                        >
                          {item.count} users
                        </Badge>
                      </div>
                    ))}
                  </div>
                </div>
              </CardContent>
            </Card>
          )}
        </div>

        {/* Detailed Tabs */}
        <Card className="bg-card/50 border-white/10">
          <CardContent className="p-3 sm:p-6">
            <Tabs defaultValue="users" className="w-full">
              <TabsList className="grid w-full grid-cols-4 sm:grid-cols-5 gap-1 h-auto">
                <TabsTrigger value="users" data-testid="admin-tab-users" className="text-xs sm:text-sm py-2">Users</TabsTrigger>
                <TabsTrigger value="bank-transfers" data-testid="admin-tab-bank-transfers" className="relative text-xs sm:text-sm py-2">
                  <span className="hidden sm:inline">Bank Transfers</span>
                  <span className="sm:hidden">Bank</span>
                  {bankTransfers.filter(t => t.status === 'pending').length > 0 && (
                    <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full w-4 h-4 sm:w-5 sm:h-5 flex items-center justify-center text-[10px] sm:text-xs">
                      {bankTransfers.filter(t => t.status === 'pending').length}
                    </span>
                  )}
                </TabsTrigger>
                <TabsTrigger value="payments" data-testid="admin-tab-payments" className="text-xs sm:text-sm py-2">
                  <span className="hidden sm:inline">Payments</span>
                  <span className="sm:hidden">Pay</span>
                </TabsTrigger>
                <TabsTrigger value="plans" data-testid="admin-tab-plans" className="text-xs sm:text-sm py-2">Plans</TabsTrigger>
                <TabsTrigger value="announcements" data-testid="admin-tab-announcements" className="text-xs sm:text-sm py-2">Announce</TabsTrigger>
                <TabsTrigger value="logs" data-testid="admin-tab-logs" className="text-xs sm:text-sm py-2">Logs</TabsTrigger>
              </TabsList>
              
              {/* Mobile secondary tabs */}
              <TabsList className="grid w-full grid-cols-3 gap-1 mt-2 sm:hidden h-auto">
                <TabsTrigger value="plans" data-testid="admin-tab-plans-mobile" className="text-xs py-2">Plans</TabsTrigger>
                <TabsTrigger value="announcements" data-testid="admin-tab-announcements-mobile" className="text-xs py-2">Announce</TabsTrigger>
                <TabsTrigger value="logs" data-testid="admin-tab-logs-mobile" className="text-xs py-2">Logs</TabsTrigger>
              </TabsList>

              {/* Users Tab */}
              <TabsContent value="users" className="mt-4 sm:mt-6">
                <div className="mb-4 relative">
                  <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                  <Input
                    type="search"
                    placeholder="Search users by name, email, phone, role, plan or status..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="pl-9"
                    data-testid="admin-users-search-input"
                  />
                  {q && (
                    <p className="text-xs text-muted-foreground mt-1" data-testid="admin-users-search-count">
                      Showing {filteredUsers.length} of {users.length} users
                    </p>
                  )}
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full min-w-[600px]">
                    <thead>
                      <tr className="border-b border-white/10">
                        <th className="text-left p-2 sm:p-3 font-heading uppercase text-xs sm:text-sm">Name</th>
                        <th className="text-left p-2 sm:p-3 font-heading uppercase text-xs sm:text-sm">Email</th>
                        <th className="text-left p-2 sm:p-3 font-heading uppercase text-xs sm:text-sm">Role</th>
                        <th className="text-left p-2 sm:p-3 font-heading uppercase text-xs sm:text-sm">Status</th>
                        <th className="text-left p-2 sm:p-3 font-heading uppercase text-xs sm:text-sm">Plan</th>
                        <th className="text-left p-2 sm:p-3 font-heading uppercase text-xs sm:text-sm">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredUsers.length === 0 ? (
                        <tr>
                          <td colSpan="6" className="p-6 text-center text-muted-foreground text-sm" data-testid="admin-users-empty">
                            {q ? 'No users match your search.' : 'No users found.'}
                          </td>
                        </tr>
                      ) : (
                        filteredUsers.map((user) => (
                        <tr key={user.id} className="border-b border-white/5 hover:bg-white/5" data-testid="admin-user-row">
                          <td className="p-2 sm:p-3 text-sm">{user.full_name}</td>
                          <td className="p-2 sm:p-3 text-muted-foreground text-sm">{user.email}</td>
                          <td className="p-2 sm:p-3">
                            <Badge
                              variant={user.role === 'admin' ? 'default' : user.role === 'trainer' ? 'secondary' : 'outline'}
                              className={`text-xs ${user.role === 'admin' ? 'bg-primary' : user.role === 'trainer' ? 'bg-secondary text-black' : ''}`}
                            >
                              {user.role}
                            </Badge>
                          </td>
                          <td className="p-2 sm:p-3">
                            <Badge
                              variant={user.subscription_status === 'active' ? 'default' : 'secondary'}
                              className={`text-xs ${user.subscription_status === 'active' ? 'bg-green-500' : user.subscription_status === 'paused' ? 'bg-yellow-500' : ''}`}
                            >
                              {user.subscription_status}
                            </Badge>
                          </td>
                          <td className="p-3 text-muted-foreground">{user.subscription_plan || '-'}</td>
                          <td className="p-3">
                            {user.role !== 'admin' && (
                              <div className="flex flex-wrap gap-2">
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => handleUpdateSubscription(
                                    user.id, 
                                    user.subscription_status === 'active' ? 'inactive' : 'active',
                                    user.subscription_plan
                                  )}
                                >
                                  {user.subscription_status === 'active' ? 'Deactivate' : 'Activate'}
                                </Button>
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => {
                                    setSelectedUserForReset(user);
                                    setNewPasswordForUser('');
                                    setResetPasswordDialogOpen(true);
                                  }}
                                  className="border-yellow-500 text-yellow-500 hover:bg-yellow-500 hover:text-black"
                                  data-testid="reset-password-btn"
                                >
                                  <KeyRound className="w-4 h-4 mr-1" />
                                  Reset
                                </Button>
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => {
                                    setSelectedUserForAccess(user);
                                    setGeneratedAccessCode(null);
                                    setAccessCodeDialogOpen(true);
                                  }}
                                  className="border-green-500 text-green-500 hover:bg-green-500 hover:text-black"
                                  data-testid="generate-access-btn"
                                >
                                  <Key className="w-4 h-4 mr-1" />
                                  Access
                                </Button>
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => {
                                    setSelectedUserForPlan(user);
                                    setSelectedPlan(user.subscription_plan || '');
                                    setPlanDays('');
                                    setChangePlanDialogOpen(true);
                                  }}
                                  className="border-blue-500 text-blue-500 hover:bg-blue-500 hover:text-black"
                                  data-testid="change-plan-btn"
                                >
                                  Change Plan
                                </Button>
                                <Select
                                  value={user.role}
                                  onValueChange={(value) => handleUpdateUserRole(user.id, value)}
                                >
                                  <SelectTrigger className="w-28 h-8">
                                    <UserCog className="w-4 h-4 mr-1" />
                                    <SelectValue />
                                  </SelectTrigger>
                                  <SelectContent>
                                    <SelectItem value="member">Member</SelectItem>
                                    <SelectItem value="trainer">Trainer</SelectItem>
                                  </SelectContent>
                                </Select>
                              </div>
                            )}
                          </td>
                        </tr>
                      ))
                      )}
                    </tbody>
                  </table>
                </div>
              </TabsContent>

              {/* Bank Transfers Tab */}
              <TabsContent value="bank-transfers" className="mt-6">
                <div className="space-y-4">
                  <div className="mb-4 relative">
                    <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                    <Input
                      type="search"
                      placeholder="Search by member name, email, depositor, plan or status..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      className="pl-9"
                      data-testid="admin-transfers-search-input"
                    />
                  </div>
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="font-heading uppercase text-lg flex items-center gap-2">
                      <Building2 className="w-5 h-5 text-primary" />
                      Bank Transfer Payments
                    </h3>
                    <Badge variant="outline">
                      {bankTransfers.filter(t => t.status === 'pending').length} Pending
                    </Badge>
                  </div>
                  
                  {filteredBankTransfers.length === 0 ? (
                    <p className="text-muted-foreground text-center py-8" data-testid="admin-transfers-empty">
                      {q ? 'No transfers match your search.' : 'No bank transfer requests yet.'}
                    </p>
                  ) : (
                    <div className="space-y-3">
                      {filteredBankTransfers.map((transfer) => (
                        <div
                          key={transfer.id}
                          className={`border rounded-lg p-4 ${
                            transfer.status === 'pending' 
                              ? 'border-yellow-500/50 bg-yellow-500/10' 
                              : transfer.status === 'approved' 
                              ? 'border-green-500/30 bg-green-500/5' 
                              : 'border-red-500/30 bg-red-500/5'
                          }`}
                          data-testid="bank-transfer-row"
                        >
                          <div className="flex justify-between items-start">
                            <div className="flex-1">
                              <div className="flex items-center gap-2 mb-2">
                                <h4 className="font-semibold">{transfer.user_name}</h4>
                                <Badge
                                  variant={transfer.status === 'pending' ? 'outline' : transfer.status === 'approved' ? 'default' : 'destructive'}
                                  className={
                                    transfer.status === 'pending' ? 'border-yellow-500 text-yellow-500' :
                                    transfer.status === 'approved' ? 'bg-green-500' : ''
                                  }
                                >
                                  {transfer.status.toUpperCase()}
                                </Badge>
                              </div>
                              <p className="text-sm text-muted-foreground">{transfer.user_email}</p>
                              <div className="grid grid-cols-2 gap-4 mt-3">
                                <div>
                                  <p className="text-xs text-muted-foreground">Plan</p>
                                  <p className="font-medium">{transfer.plan_name}</p>
                                </div>
                                <div>
                                  <p className="text-xs text-muted-foreground">Amount</p>
                                  <p className="font-bold text-primary">₦{transfer.amount?.toLocaleString()}</p>
                                </div>
                                <div>
                                  <p className="text-xs text-muted-foreground">Depositor Name</p>
                                  <p className="font-medium">{transfer.depositor_name}</p>
                                </div>
                                <div>
                                  <p className="text-xs text-muted-foreground">Submitted</p>
                                  <p className="font-medium">{new Date(transfer.created_at).toLocaleString()}</p>
                                </div>
                              </div>
                              {transfer.proof_url && (
                                <a 
                                  href={transfer.proof_url} 
                                  target="_blank" 
                                  rel="noopener noreferrer"
                                  className="inline-flex items-center gap-1 mt-2 text-sm text-primary hover:underline"
                                >
                                  <Eye className="w-4 h-4" />
                                  View Proof of Payment
                                </a>
                              )}
                              {transfer.admin_notes && (
                                <p className="text-sm text-muted-foreground mt-2 italic">
                                  Notes: {transfer.admin_notes}
                                </p>
                              )}
                            </div>
                            
                            {transfer.status === 'pending' && (
                              <div className="flex gap-2 ml-4">
                                <Button
                                  size="sm"
                                  onClick={() => handleApproveBankTransfer(transfer.id)}
                                  className="bg-green-500 hover:bg-green-600"
                                  data-testid="approve-transfer-btn"
                                >
                                  <CheckCircle className="w-4 h-4 mr-1" />
                                  Approve
                                </Button>
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => handleRejectBankTransfer(transfer.id)}
                                  className="border-red-500 text-red-500 hover:bg-red-500 hover:text-white"
                                  data-testid="reject-transfer-btn"
                                >
                                  <XCircle className="w-4 h-4 mr-1" />
                                  Reject
                                </Button>
                              </div>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </TabsContent>

              {/* Payments Tab */}
              <TabsContent value="payments" className="mt-6">
                <div className="mb-4 relative">
                  <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                  <Input
                    type="search"
                    placeholder="Search payments by user, email, amount or status..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="pl-9"
                    data-testid="admin-payments-search-input"
                  />
                </div>
                <div className="space-y-3">
                  {filteredPayments.length === 0 ? (
                    <p className="text-muted-foreground text-center py-8" data-testid="admin-payments-empty">
                      {q ? 'No payments match your search.' : 'No payments yet.'}
                    </p>
                  ) : filteredPayments.map((payment) => (
                    <div
                      key={payment.id}
                      className="flex justify-between items-center border-b border-white/5 pb-3"
                      data-testid="admin-payment-row"
                    >
                      <div>
                        <p className="font-semibold">{payment.user_name || 'Unknown User'}</p>
                        <p className="text-sm text-muted-foreground">{payment.user_email}</p>
                        <p className="text-xs text-muted-foreground">{new Date(payment.created_at).toLocaleString()}</p>
                      </div>
                      <div className="text-right">
                        <p className="font-bold">₦{payment.amount.toLocaleString()}</p>
                        <Badge
                          variant={payment.payment_status === 'paid' ? 'default' : 'secondary'}
                          className={payment.payment_status === 'paid' ? 'bg-green-500' : ''}
                        >
                          {payment.payment_status}
                        </Badge>
                      </div>
                    </div>
                  ))}
                </div>
              </TabsContent>

              {/* Subscription Plans Tab */}
              <TabsContent value="plans" className="mt-6">
                <div className="space-y-4">
                  <div className="flex justify-between items-center mb-4">
                    <h3 className="font-heading uppercase text-lg">Subscription Plans</h3>
                  </div>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {subscriptionPlans.map((plan) => (
                      <Card key={plan.id} className="bg-card/50 border-white/10">
                        <CardHeader>
                          <CardTitle className="flex justify-between items-center">
                            <span>{plan.name}</span>
                            <Badge variant={plan.is_active ? 'default' : 'secondary'} className={plan.is_active ? 'bg-green-500' : ''}>
                              {plan.is_active ? 'Active' : 'Inactive'}
                            </Badge>
                          </CardTitle>
                          <CardDescription>{plan.description}</CardDescription>
                        </CardHeader>
                        <CardContent>
                          <div className="space-y-2">
                            <div className="flex justify-between">
                              <span className="text-muted-foreground">Price:</span>
                              <span className="font-bold text-xl">₦{plan.price?.toLocaleString()}</span>
                            </div>
                            <div className="flex justify-between">
                              <span className="text-muted-foreground">Duration:</span>
                              <span className="font-semibold">{plan.duration_days} days</span>
                            </div>
                          </div>
                          <Button
                            variant="outline"
                            className="w-full mt-4 border-primary text-primary hover:bg-primary hover:text-white"
                            onClick={() => {
                              setEditingPlan(plan);
                              setPlanForm({
                                name: plan.name || '',
                                price: plan.price?.toString() || '',
                                duration_days: plan.duration_days?.toString() || '',
                                description: plan.description || ''
                              });
                              setPlansDialogOpen(true);
                            }}
                            data-testid="edit-plan-btn"
                          >
                            <Edit className="w-4 h-4 mr-2" />
                            Edit Plan
                          </Button>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                  
                  {subscriptionPlans.length === 0 && (
                    <div className="text-center py-8 text-muted-foreground">
                      No subscription plans found. Plans are created during system initialization.
                    </div>
                  )}
                </div>
              </TabsContent>

              {/* Access Logs Tab */}
              <TabsContent value="logs" className="mt-6">
                <div className="mb-4 relative">
                  <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                  <Input
                    type="search"
                    placeholder="Search logs by user, method or status..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="pl-9"
                    data-testid="admin-logs-search-input"
                  />
                </div>
                <div className="space-y-2">
                  {filteredAccessLogs.length === 0 ? (
                    <p className="text-muted-foreground text-center py-8" data-testid="admin-logs-empty">
                      {q ? 'No logs match your search.' : 'No access logs yet.'}
                    </p>
                  ) : filteredAccessLogs.map((log) => (
                    <div
                      key={log.id}
                      className="flex justify-between items-center border-b border-white/5 pb-2"
                      data-testid="admin-access-log"
                    >
                      <div>
                        <p className="font-semibold">{log.user_name || 'Unknown User'}</p>
                        <p className="text-xs text-muted-foreground">
                          {new Date(log.timestamp).toLocaleString()}
                        </p>
                      </div>
                      <div className="text-right">
                        <p className="text-sm capitalize">{log.access_method}</p>
                        <Badge
                          variant={log.access_status === 'granted' ? 'default' : 'destructive'}
                          className={log.access_status === 'granted' ? 'bg-green-500' : ''}
                        >
                          {log.access_status}
                        </Badge>
                      </div>
                    </div>
                  ))}
                </div>
              </TabsContent>

              {/* Announcements Tab */}
              <TabsContent value="announcements" className="mt-6">
                <div className="flex justify-between items-center mb-4">
                  <h3 className="font-heading uppercase text-lg">Manage Announcements</h3>
                  <Dialog open={announcementDialogOpen} onOpenChange={setAnnouncementDialogOpen}>
                    <DialogTrigger asChild>
                      <Button
                        onClick={() => {
                          setEditingAnnouncement(null);
                          setAnnouncementForm({ title: '', content: '', priority: 'normal', is_published: true });
                        }}
                        data-testid="create-announcement-btn"
                      >
                        <Plus className="w-4 h-4 mr-2" />
                        New Announcement
                      </Button>
                    </DialogTrigger>
                    <DialogContent className="sm:max-w-[500px]">
                      <DialogHeader>
                        <DialogTitle>{editingAnnouncement ? 'Edit Announcement' : 'Create Announcement'}</DialogTitle>
                        <DialogDescription>
                          {editingAnnouncement ? 'Update the announcement details.' : 'Create a new announcement for your members.'}
                        </DialogDescription>
                      </DialogHeader>
                      <div className="space-y-4 py-4">
                        <div>
                          <Label htmlFor="announcement-title">Title</Label>
                          <Input
                            id="announcement-title"
                            value={announcementForm.title}
                            onChange={(e) => setAnnouncementForm({ ...announcementForm, title: e.target.value })}
                            placeholder="Announcement title"
                            className="mt-1"
                          />
                        </div>
                        <div>
                          <Label htmlFor="announcement-content">Content</Label>
                          <Textarea
                            id="announcement-content"
                            value={announcementForm.content}
                            onChange={(e) => setAnnouncementForm({ ...announcementForm, content: e.target.value })}
                            placeholder="Announcement content..."
                            rows={4}
                            className="mt-1"
                          />
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <Label>Priority</Label>
                            <Select
                              value={announcementForm.priority}
                              onValueChange={(value) => setAnnouncementForm({ ...announcementForm, priority: value })}
                            >
                              <SelectTrigger className="mt-1">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="low">Low</SelectItem>
                                <SelectItem value="normal">Normal</SelectItem>
                                <SelectItem value="high">High</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                          <div>
                            <Label>Status</Label>
                            <Select
                              value={announcementForm.is_published ? 'published' : 'draft'}
                              onValueChange={(value) => setAnnouncementForm({ ...announcementForm, is_published: value === 'published' })}
                            >
                              <SelectTrigger className="mt-1">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="published">Published</SelectItem>
                                <SelectItem value="draft">Draft</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                        </div>
                      </div>
                      <DialogFooter>
                        <Button
                          onClick={editingAnnouncement ? handleUpdateAnnouncement : handleCreateAnnouncement}
                          data-testid="save-announcement-btn"
                        >
                          {editingAnnouncement ? 'Update' : 'Create'}
                        </Button>
                      </DialogFooter>
                    </DialogContent>
                  </Dialog>
                </div>
                
                <div className="space-y-3">
                  {announcements.length === 0 ? (
                    <p className="text-muted-foreground text-center py-8">No announcements yet. Create one!</p>
                  ) : (
                    announcements.map((announcement) => (
                      <div
                        key={announcement.id}
                        className="border border-white/10 rounded-lg p-4 hover:border-white/20 transition-colors"
                        data-testid="admin-announcement-row"
                      >
                        <div className="flex justify-between items-start">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-2">
                              <h4 className="font-semibold">{announcement.title}</h4>
                              <Badge
                                variant={announcement.is_published ? 'default' : 'secondary'}
                                className={announcement.is_published ? 'bg-green-500' : ''}
                              >
                                {announcement.is_published ? 'Published' : 'Draft'}
                              </Badge>
                              <Badge
                                variant="outline"
                                className={
                                  announcement.priority === 'high' ? 'border-red-500 text-red-500' :
                                  announcement.priority === 'normal' ? 'border-blue-500 text-blue-500' :
                                  'border-gray-500 text-gray-500'
                                }
                              >
                                {announcement.priority}
                              </Badge>
                            </div>
                            <p className="text-sm text-muted-foreground mb-2">{announcement.content}</p>
                            <p className="text-xs text-muted-foreground">
                              Created: {new Date(announcement.created_at).toLocaleDateString()}
                            </p>
                          </div>
                          <div className="flex gap-2 ml-4">
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => {
                                setEditingAnnouncement(announcement);
                                setAnnouncementForm({
                                  title: announcement.title,
                                  content: announcement.content,
                                  priority: announcement.priority,
                                  is_published: announcement.is_published
                                });
                                setAnnouncementDialogOpen(true);
                              }}
                            >
                              <Edit className="w-4 h-4" />
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              className="border-destructive text-destructive hover:bg-destructive hover:text-white"
                              onClick={() => handleDeleteAnnouncement(announcement.id)}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </TabsContent>
            </Tabs>
          </CardContent>
        </Card>
      </div>

      {/* Reset Password Dialog */}
      <Dialog open={resetPasswordDialogOpen} onOpenChange={setResetPasswordDialogOpen}>
        <DialogContent className="w-[95vw] max-w-[400px]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <KeyRound className="w-5 h-5 text-yellow-500" />
              Reset User Password
            </DialogTitle>
            <DialogDescription>
              Reset password for {selectedUserForReset?.full_name} ({selectedUserForReset?.email})
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label htmlFor="new-user-password">New Password</Label>
              <Input
                id="new-user-password"
                type="text"
                value={newPasswordForUser}
                onChange={(e) => setNewPasswordForUser(e.target.value)}
                placeholder="Enter new password"
                className="mt-1"
                data-testid="new-user-password-input"
              />
              <p className="text-xs text-muted-foreground mt-1">
                Minimum 6 characters. User will receive email notification.
              </p>
            </div>
          </div>
          <DialogFooter className="flex-col sm:flex-row gap-2">
            <Button
              variant="outline"
              onClick={() => setResetPasswordDialogOpen(false)}
              className="w-full sm:w-auto"
            >
              Cancel
            </Button>
            <Button
              onClick={handleResetUserPassword}
              disabled={resettingPassword || !newPasswordForUser}
              className="w-full sm:w-auto bg-yellow-500 hover:bg-yellow-600 text-black"
              data-testid="confirm-reset-password-btn"
            >
              {resettingPassword ? 'Resetting...' : 'Reset Password'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Generate Access Code Dialog */}
      <Dialog open={accessCodeDialogOpen} onOpenChange={(open) => {
        setAccessCodeDialogOpen(open);
        if (!open) {
          setGeneratedAccessCode(null);
        }
      }}>
        <DialogContent className="w-[95vw] max-w-[500px]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Key className="w-5 h-5 text-green-500" />
              Generate Access Code
            </DialogTitle>
            <DialogDescription>
              Generate OTP, temporary password, or unlock door for {selectedUserForAccess?.full_name} ({selectedUserForAccess?.email})
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            {!generatedAccessCode ? (
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <Button
                  onClick={() => handleGenerateAccessCode('otp')}
                  disabled={generatingAccess}
                  className="flex flex-col items-center gap-2 h-20 bg-blue-600 hover:bg-blue-700"
                  data-testid="generate-otp-btn"
                >
                  <Key className="w-6 h-6" />
                  <span className="text-sm">Generate OTP</span>
                </Button>
                <Button
                  onClick={() => handleGenerateAccessCode('temp_password')}
                  disabled={generatingAccess}
                  className="flex flex-col items-center gap-2 h-20 bg-purple-600 hover:bg-purple-700"
                  data-testid="generate-temp-password-btn"
                >
                  <KeyRound className="w-6 h-6" />
                  <span className="text-sm">Temp Password</span>
                </Button>
                <Button
                  onClick={() => handleGenerateAccessCode('unlock')}
                  disabled={generatingAccess}
                  className="flex flex-col items-center gap-2 h-20 bg-green-600 hover:bg-green-700"
                  data-testid="unlock-door-btn"
                >
                  <Lock className="w-6 h-6" />
                  <span className="text-sm">Unlock Door</span>
                </Button>
              </div>
            ) : (
              <div className="bg-card border border-green-500/30 rounded-lg p-4 space-y-3">
                <div className="flex items-center gap-2 text-green-500">
                  <CheckCircle className="w-5 h-5" />
                  <span className="font-semibold">Access Code Generated</span>
                </div>
                
                <div className="space-y-2">
                  <div className="flex justify-between items-center">
                    <span className="text-muted-foreground">User:</span>
                    <span className="font-medium">{generatedAccessCode.user_name}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-muted-foreground">Type:</span>
                    <Badge variant="outline" className="uppercase">{generatedAccessCode.access_type}</Badge>
                  </div>
                  
                  {generatedAccessCode.otp && (
                    <div className="mt-4 p-4 bg-blue-500/20 rounded-lg text-center">
                      <p className="text-sm text-muted-foreground mb-1">OTP Code</p>
                      <p className="text-3xl font-mono font-bold tracking-widest text-blue-400" data-testid="generated-otp">
                        {generatedAccessCode.otp}
                      </p>
                      <p className="text-xs text-muted-foreground mt-2">
                        Expires in {generatedAccessCode.expires_in / 60} minutes
                      </p>
                    </div>
                  )}
                  
                  {generatedAccessCode.password && (
                    <div className="mt-4 p-4 bg-purple-500/20 rounded-lg text-center">
                      <p className="text-sm text-muted-foreground mb-1">Temporary Password</p>
                      <p className="text-3xl font-mono font-bold tracking-widest text-purple-400" data-testid="generated-temp-password">
                        {generatedAccessCode.password}
                      </p>
                      <p className="text-xs text-muted-foreground mt-2">
                        Valid for 24 hours
                      </p>
                    </div>
                  )}
                  
                  {generatedAccessCode.access_type === 'unlock' && (
                    <div className={`mt-4 p-4 rounded-lg text-center ${generatedAccessCode.door_unlocked ? 'bg-green-500/20' : 'bg-red-500/20'}`}>
                      <p className="text-sm text-muted-foreground mb-1">Door Status</p>
                      <p className={`text-xl font-bold ${generatedAccessCode.door_unlocked ? 'text-green-400' : 'text-red-400'}`}>
                        {generatedAccessCode.door_unlocked ? 'UNLOCKED' : 'UNLOCK FAILED'}
                      </p>
                      <p className="text-xs text-muted-foreground mt-2">
                        {generatedAccessCode.message}
                      </p>
                    </div>
                  )}
                </div>
              </div>
            )}
            
            {generatingAccess && (
              <div className="text-center py-4">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto"></div>
                <p className="text-sm text-muted-foreground mt-2">Generating...</p>
              </div>
            )}
          </div>
          
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setAccessCodeDialogOpen(false);
                setGeneratedAccessCode(null);
              }}
              className="w-full sm:w-auto"
            >
              Close
            </Button>
            {generatedAccessCode && (
              <Button
                onClick={() => setGeneratedAccessCode(null)}
                className="w-full sm:w-auto"
              >
                Generate Another
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Generate Shared Daily Code Dialog */}
      <Dialog open={sharedCodeDialogOpen} onOpenChange={(open) => {
        setSharedCodeDialogOpen(open);
        if (!open) setGeneratedSharedCode(null);
      }}>
        <DialogContent className="w-[95vw] max-w-[500px]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Key className="w-5 h-5 text-green-500" />
              Generate Daily Access Code
            </DialogTitle>
            <DialogDescription>
              Enter a 6-digit code or auto-generate one. This code will be visible to all users with active subscriptions.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            {!generatedSharedCode ? (
              <>
                {/* Manual Code Input */}
                <div className="bg-card/50 border border-green-500/30 rounded-lg p-4">
                  <Label htmlFor="custom-code" className="text-green-400 font-semibold">Enter 6-7 Digit Code</Label>
                  <p className="text-xs text-muted-foreground mb-2">Type the access code you want to share with all active members</p>
                  <Input
                    id="custom-code"
                    value={customCode}
                    onChange={(e) => {
                      const value = e.target.value.replace(/\D/g, '').slice(0, 7);
                      setCustomCode(value);
                    }}
                    placeholder="Enter 6-7 digits (e.g., 123456)"
                    className="text-2xl font-mono tracking-[0.3em] text-center h-14 bg-background/50 border-green-500/50"
                    maxLength={7}
                    data-testid="custom-code-input"
                  />
                  {customCode && customCode.length < 6 && (
                    <p className="text-xs text-yellow-500 mt-1">Enter at least 6 digits ({6 - customCode.length} more needed)</p>
                  )}
                  {customCode && customCode.length >= 6 && customCode.length <= 7 && (
                    <p className="text-xs text-green-500 mt-1">✓ Code ready to share</p>
                  )}
                </div>

                <div className="text-center text-muted-foreground text-sm">— OR —</div>

                <Button
                  variant="outline"
                  onClick={() => {
                    const autoCode = String(Math.floor(100000 + Math.random() * 900000));
                    setCustomCode(autoCode);
                  }}
                  className="w-full border-blue-500/50 text-blue-400 hover:bg-blue-500/20"
                >
                  Auto-Generate Random Code
                </Button>

                <div>
                  <Label htmlFor="hours-valid">Valid for (hours)</Label>
                  <Select
                    value={sharedCodeHours.toString()}
                    onValueChange={(value) => setSharedCodeHours(parseInt(value))}
                  >
                    <SelectTrigger className="w-full mt-1">
                      <SelectValue placeholder="Select duration" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="1">1 hour</SelectItem>
                      <SelectItem value="6">6 hours</SelectItem>
                      <SelectItem value="12">12 hours</SelectItem>
                      <SelectItem value="24">24 hours (1 day)</SelectItem>
                      <SelectItem value="48">48 hours (2 days)</SelectItem>
                      <SelectItem value="168">168 hours (1 week)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                
                <div>
                  <Label htmlFor="code-message">Message (optional)</Label>
                  <Input
                    id="code-message"
                    value={sharedCodeMessage}
                    onChange={(e) => setSharedCodeMessage(e.target.value)}
                    placeholder="e.g., Morning session code"
                    className="mt-1"
                  />
                </div>
                
                <Button
                  onClick={handleGenerateSharedCode}
                  disabled={generatingSharedCode || !customCode || customCode.length < 6 || customCode.length > 7}
                  className="w-full bg-green-600 hover:bg-green-700 h-12"
                  data-testid="confirm-set-code-btn"
                >
                  {generatingSharedCode ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                      Setting Code...
                    </>
                  ) : (
                    <>
                      <Key className="w-4 h-4 mr-2" />
                      Set Code for All Active Members
                    </>
                  )}
                </Button>
              </>
            ) : (
              <div className="bg-gradient-to-r from-green-500/20 to-blue-500/20 border border-green-500/30 rounded-lg p-6 text-center">
                <CheckCircle className="w-12 h-12 text-green-500 mx-auto mb-4" />
                <p className="text-sm text-muted-foreground mb-2">Daily Access Code Generated</p>
                <p className="text-4xl font-mono font-bold text-green-400 tracking-[0.3em] mb-4" data-testid="generated-shared-code">
                  {generatedSharedCode.code}
                </p>
                <div className="space-y-1 text-sm text-muted-foreground">
                  <p>Valid for: {sharedCodeHours} hours</p>
                  <p>Expires: {new Date(generatedSharedCode.expires_at).toLocaleString()}</p>
                  {generatedSharedCode.door_unlock_attempted && (
                    <p className="text-green-500">Door unlock command sent</p>
                  )}
                </div>
                <p className="mt-4 text-sm text-green-400">
                  All users can now see this code on their dashboard!
                </p>
              </div>
            )}
          </div>
          
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setSharedCodeDialogOpen(false);
                setGeneratedSharedCode(null);
              }}
              className="w-full sm:w-auto"
            >
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Change Plan Dialog */}
      <Dialog open={changePlanDialogOpen} onOpenChange={setChangePlanDialogOpen}>
        <DialogContent className="bg-card border-white/10 max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              Change Subscription Plan
            </DialogTitle>
            <DialogDescription>
              Change plan for {selectedUserForPlan?.full_name || selectedUserForPlan?.email}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Select Plan</Label>
              <Select value={selectedPlan} onValueChange={setSelectedPlan}>
                <SelectTrigger>
                  <SelectValue placeholder="Select a plan" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="per_session">Per Session (₦2,000)</SelectItem>
                  <SelectItem value="weekly">Weekly (₦6,000)</SelectItem>
                  <SelectItem value="monthly">Monthly (₦18,000)</SelectItem>
                  <SelectItem value="quarterly">Quarterly (₦48,000)</SelectItem>
                  <SelectItem value="yearly">Yearly (₦180,000)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div className="space-y-2">
              <Label>Custom Duration (Optional)</Label>
              <Input
                type="number"
                placeholder="Number of days (leave empty for default)"
                value={planDays}
                onChange={(e) => setPlanDays(e.target.value)}
              />
              <p className="text-xs text-muted-foreground">
                Default durations: Per Session (1 day), Weekly (7 days), Monthly (30 days), Quarterly (90 days), Yearly (365 days)
              </p>
            </div>
            
            {selectedUserForPlan && (
              <div className="bg-muted/50 p-3 rounded-lg">
                <p className="text-sm">Current Plan: <span className="font-medium">{selectedUserForPlan.subscription_plan || 'None'}</span></p>
                <p className="text-sm">Current Status: <span className={`font-medium ${selectedUserForPlan.subscription_status === 'active' ? 'text-green-500' : 'text-yellow-500'}`}>
                  {selectedUserForPlan.subscription_status || 'Inactive'}
                </span></p>
              </div>
            )}
          </div>
          
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setChangePlanDialogOpen(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleChangePlan}
              disabled={!selectedPlan}
              className="bg-blue-600 hover:bg-blue-700"
            >
              Update Plan & Activate
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Plan Dialog */}
      <Dialog open={plansDialogOpen} onOpenChange={setPlansDialogOpen}>
        <DialogContent className="bg-card border-white/10 max-w-md">
          <DialogHeader>
            <DialogTitle>Edit Subscription Plan</DialogTitle>
            <DialogDescription>
              Update the plan details and pricing
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="plan-name">Plan Name</Label>
              <Input
                id="plan-name"
                value={planForm.name}
                onChange={(e) => setPlanForm({ ...planForm, name: e.target.value })}
                placeholder="e.g., Monthly Subscription"
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="plan-price">Price (₦)</Label>
              <Input
                id="plan-price"
                type="number"
                value={planForm.price}
                onChange={(e) => setPlanForm({ ...planForm, price: e.target.value })}
                placeholder="e.g., 18000"
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="plan-duration">Duration (Days)</Label>
              <Input
                id="plan-duration"
                type="number"
                value={planForm.duration_days}
                onChange={(e) => setPlanForm({ ...planForm, duration_days: e.target.value })}
                placeholder="e.g., 30"
              />
              <p className="text-xs text-muted-foreground">
                User subscription will expire exactly after this many days
              </p>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="plan-description">Description</Label>
              <Input
                id="plan-description"
                value={planForm.description}
                onChange={(e) => setPlanForm({ ...planForm, description: e.target.value })}
                placeholder="Plan description"
              />
            </div>
          </div>
          
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setPlansDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleUpdatePlan} className="bg-primary">
              Update Plan
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminDashboard;
